import { useEffect, useState } from 'react';
import { api, type AdminRun, type HermesStatus, type RunSlots, type WarmSession } from '../api';
import { useAuth } from '../auth';
import { Page, Card, Button, Badge, EmptyState } from '../ui';

export function ActiveAgents() {
  const { me } = useAuth();
  const [runs, setRuns] = useState<AdminRun[]>([]);
  const [slots, setSlots] = useState<RunSlots | null>(null);
  const [sessions, setSessions] = useState<WarmSession[]>([]);
  const [ttlHours, setTtlHours] = useState(48);
  const [now, setNow] = useState(Date.now());
  const [err, setErr] = useState<string | null>(null);
  const [hermes, setHermes] = useState<HermesStatus | null>(null);

  function reload() {
    api
      .listRuns()
      .then(({ runs, slots }) => {
        setRuns(runs);
        setSlots(slots);
      })
      .catch(() => setErr('Falha ao carregar execuções'));
    api
      .getHermesStatus()
      .then(({ hermes }) => setHermes(hermes))
      .catch(() => undefined);
    api
      .listSessions()
      .then(({ sessions, ttlHours }) => {
        setSessions(sessions);
        setTtlHours(ttlHours);
      })
      .catch(() => undefined);
  }

  useEffect(() => {
    if (me?.globalRole !== 'admin') return;
    reload();
    const t = setInterval(reload, 4000);
    const tick = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      clearInterval(t);
      clearInterval(tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me?.globalRole]);

  if (me?.globalRole !== 'admin') {
    return (
      <Page title="Agentes ativos">
        <EmptyState title="Acesso restrito" description="Apenas administradores." />
      </Page>
    );
  }

  async function kill(id: string) {
    await api.killRun(id).catch(() => undefined);
    reload();
  }
  async function expire(threadId: string) {
    await api.expireSession(threadId).catch(() => undefined);
    reload();
  }

  return (
    <Page
      title="Agentes ativos"
      subtitle="Execuções do Claude rodando agora e sessões de chat com TTL."
      badges={
        slots ? (
          <Badge variant={slots.active >= slots.max ? 'warn' : 'ok'} className="ml-2">
            {slots.active}/{slots.max} slots{slots.waiting > 0 ? ` · ${slots.waiting} na fila` : ''}
          </Badge>
        ) : null
      }
    >
      {err && <div className="chat-error">{err}</div>}

      {/* Harness Hermes (apartado, em outra VPS) — status da conexão + config. */}
      <Card style={{ marginBottom: 16 }}>
        <div className="card-title-row">
          <strong>Hermes (harness)</strong>
          {hermes && (
            <Badge variant={hermes.health === 'online' ? 'ok' : hermes.health === 'disabled' ? 'accent' : 'warn'}>
              {hermes.health}
            </Badge>
          )}
        </div>
        {!hermes ? (
          <div className="users-muted" style={{ padding: '8px 0' }}>Carregando…</div>
        ) : !hermes.enabled ? (
          <div className="users-muted" style={{ padding: '8px 0' }}>
            Não configurado (defina HERMES_URL e HERMES_API_KEY).
          </div>
        ) : (
          <table className="users-table">
            <tbody>
              <tr><td>URL (via túnel)</td><td className="users-muted">{hermes.url}</td></tr>
              <tr><td>Modelo</td><td>{hermes.model}</td></tr>
              <tr><td>Roteador usa o Hermes</td><td>{hermes.routerUsesHermes ? 'sim' : 'não'}</td></tr>
              <tr><td>Modelos expostos</td><td className="users-muted">{hermes.models.join(', ') || '—'}</td></tr>
            </tbody>
          </table>
        )}
      </Card>

      <Card style={{ marginBottom: 16 }}>
        <div className="card-title-row"><strong>Rodando agora</strong> <span className="users-muted">{runs.length}</span></div>
        {runs.length === 0 ? (
          <div className="users-muted" style={{ padding: '8px 0' }}>Nada rodando no momento.</div>
        ) : (
          <table className="users-table">
            <thead>
              <tr><th>Tipo</th><th>Projeto</th><th>Agente</th><th>Quem</th><th>Tempo</th><th></th></tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td><Badge variant="accent">{r.kind}</Badge></td>
                  <td>{r.projectSlug ?? '—'}</td>
                  <td>{r.agentSlug ?? '—'}</td>
                  <td className="users-muted">{r.actor ?? '—'}</td>
                  <td>{fmtElapsed(now - r.startedAt)}</td>
                  <td className="users-actions">
                    <Button size="sm" variant="ghost" onClick={() => kill(r.id)}>encerrar</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <Card>
        <div className="card-title-row">
          <strong>Sessões mornas</strong> <span className="users-muted">expiram após {ttlHours}h sem uso</span>
        </div>
        {sessions.length === 0 ? (
          <div className="users-muted" style={{ padding: '8px 0' }}>Nenhuma sessão ativa.</div>
        ) : (
          <table className="users-table">
            <thead>
              <tr><th>Conversa</th><th>Projeto</th><th>Agente</th><th>Canal</th><th>Expira em</th><th></th></tr>
            </thead>
            <tbody>
              {sessions.map((s) => {
                const expiresInMs = new Date(s.updatedAt).getTime() + ttlHours * 3600_000 - now;
                return (
                  <tr key={s.id}>
                    <td>{s.title}</td>
                    <td>{s.projectSlug ?? '—'}</td>
                    <td>{s.agentName ?? '—'}</td>
                    <td>{s.channelThreadId ? 'Telegram' : 'Web'}</td>
                    <td className="users-muted">{expiresInMs > 0 ? fmtElapsed(expiresInMs) : 'expirando…'}</td>
                    <td className="users-actions">
                      <Button size="sm" variant="ghost" onClick={() => expire(s.id)}>expirar</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>
    </Page>
  );
}

function fmtElapsed(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d`;
}
